// src/components/pages/admin-dash/AdminAuditActivitySummary.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Badge from "../../ui/Badge";
import AdminTable from "./AdminTable";

/**
 * Consolidated view over AdminAuditLogResource
 * (GET /admin/audit-log/summary) — who did the most sensitive admin
 * actions in a period, broken down by action type. Pure aggregation on
 * top of the existing audit log (1-ADMIN_ROADMAP.md item 3.8) — useful
 * for periodic access reviews ("does admin X really need this much
 * standing access?").
 */
const ACTION_LABELS = {
  USER_UPDATE: "User update",
  BULK_CREDIT: "Bulk credit",
};

const todayIso = () => new Date().toISOString().split("T")[0];
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
};

export default function AdminAuditActivitySummary() {
  const [startDate, setStartDate] = useState(daysAgoIso(30));
  const [endDate, setEndDate] = useState(todayIso());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [didSearch, setDidSearch] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await axiosInstance.get("/admin/audit-log/summary", { params });
      setRows(Array.isArray(res.data) ? res.data : []);
      setDidSearch(true);
    } catch {
      setError("Failed to load activity summary.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSummary(); }, []); // eslint-disable-line

  const columns = [
    { key: "actor", label: "Admin", sort: true, value: (r) => r.actor?.name || "",
      render: (r) => (
        <div>
          <div style={{ color: "var(--txt)" }} className="font-medium">{r.actor?.name || "—"}</div>
          <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>#{r.actor?.id ?? "?"}</div>
        </div>
      ) },
    { key: "action", label: "Action", sort: true, value: (r) => r.action || "",
      render: (r) => <Badge variant="gold">{ACTION_LABELS[r.action] || r.action}</Badge> },
    { key: "totalActions", label: "Count", sort: true, align: "right",
      render: (r) => <span className="tabular-nums font-medium">{(r.totalActions ?? 0).toLocaleString()}</span> },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Activity summary
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Who did the most sensitive admin actions in a period, broken down by action type.
          Useful for periodic access reviews.
        </p>
      </header>

      <Card variant="soft" className="!p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <Field label="From" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="!mb-0" />
          <Field label="To" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="!mb-0" />
          <div className="flex items-end">
            <Button icon="search" size="sm" onClick={fetchSummary} loading={loading}>Search</Button>
          </div>
        </div>
      </Card>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
        >
          {error}
        </div>
      )}

      <AdminTable
        title={didSearch ? `${rows.length} rows` : undefined}
        columns={columns}
        rows={rows}
        loading={loading}
        emptyIcon="shield-check"
        emptyMessage="No admin activity in this period."
        defaultSort={{ key: "totalActions", dir: "desc" }}
      />
    </div>
  );
}
